/**
 * Browser template-instance lifecycle.
 *
 * A template instance clones the cached DOM shell, binds its part objects, and
 * installs one reactive effect per binding that can change. A binding that
 * provably cannot change (`isStaticBinding`) commits once and holds no effect,
 * no closure and no disposer; it gains one the moment an update hands it a
 * value that can change. Updating replaces the binding bag without rebuilding
 * the shell. Unmounting disposes effects before parts and nodes so listeners
 * and nested component lifecycles cannot leak. All actual HTML parsing remains
 * owned by the reviewed adapter supplied by render.ts.
 *
 * Mount and update commit in two passes (G10): every node-family part first,
 * then every attribute-family and element part. `effect()` runs its callback
 * synchronously on creation, so creating node-family effects first commits
 * them first. This makes `<select .value=${v}>${options}</select>` correct
 * without ordering the template by hand — the options exist before `.value`
 * is assigned, so the browser can match it against a real `<option>`.
 */
import { createOwner, disposeOwner, effect, getOwner, onCleanup, runWithOwner, untrack, } from './signal.js';
import { isSubtreeDetaching } from './render-dom-range.js';
import { prepareDomParts } from './render-dom-plan.js';
import { ensureParse, takeFragment } from './render-dom-parse.js';
import { bindingUnchanged, commitStaticSlotsBefore, updateStaticSlotsBefore, } from './render-dom-static-slots.js';
import { isDeferredPart, isStaticBinding } from './render-dom-static.js';
import { compiledNavBinder } from './render-dom-nav-hook.js';
import { isLegacyComponent, mountLegacyComponent } from './render-component-adapter.js';
import { getBinding } from './render-template.js';
import { INSTANCE_SYMBOL, isTemplateInstanceValue, runtimeError } from './render-values.js';
// The legacy `.html` getter needs the string renderer, but a static import
// would put the whole SSR string pipeline on every client graph — nucleus
// paid ~0.8 KB gzip for it. render-string.js installs this pointer as a
// module side effect, so the getter works exactly where the string renderer
// actually exists and RT411s where it does not.
let templateHtmlRenderer = null;
export function installTemplateHtmlRenderer(renderer) {
    templateHtmlRenderer = renderer;
}
function childNodesOf(node) {
    return Array.from(node.childNodes || []);
}
/**
 * Source of the per-bind identity a materialization proves ownership with.
 *
 * `instance.container` used to be that proof (#626), and it worked, but it
 * cost the thing it pointed AT: an instance mounted into its own fragment
 * pinned that fragment — and, when the fragment is a consumed template's
 * `content`, its host `<template>` and their contents-owner document with it —
 * for as long as the instance stayed mounted. A counter proves the same claim
 * and retains nothing (#1335).
 */
let nextMountEpoch = 0;
/**
 * Undoes the adapter's `<svg>` wrapper (G12): the cloned fragment's only
 * child is the wrapper element the adapter parsed the static HTML under, so
 * its own children — the actual template output, comment markers included —
 * are spliced into the fragment in its place and the wrapper is discarded.
 * A namespaced template with no matching wrapper (nothing to unwrap) is left
 * untouched rather than treated as an error.
 */
function unwrapNamespaceWrapper(fragment) {
    const wrapper = fragment.firstChild;
    if (wrapper?.nodeType !== 1)
        return fragment;
    while (wrapper.firstChild) {
        fragment.insertBefore(wrapper.firstChild, wrapper);
    }
    fragment.removeChild(wrapper);
    return fragment;
}
export class TemplateInstanceImpl {
    [INSTANCE_SYMBOL] = true;
    template;
    bindings;
    container = null;
    nodes = [];
    parts = [];
    /**
     * Slots whose mount binding is static: spec indices of holes that commit
     * once WITHOUT a part object (#1398), paired with `staticRefs` and
     * family-partitioned like `parts`. Null when the clone has none, so an
     * all-reactive template pays two nullable pointers, never two arrays.
     */
    staticIndices = null;
    /**
     * Per static slot, the DOM reference a promotion (#678) would bind through:
     * the element an attribute-family slot writes through, or the elided range
     * parent (#1354) a node-family slot commits into.
     */
    staticRefs = null;
    /** The shared plan the slots' commit state is re-derived from at commit time. */
    staticPlan = null;
    /**
     * Parts promoted out of `staticIndices` mid-update, staged here rather than
     * pushed into `parts` because the update walk holds cursors over that array
     * (see `commitChangedParts`).
     */
    promotedParts = null;
    /**
     * Internal containment hook. Not part of the public TemplateInstance ABI.
     * Installed by `catchErrors` / `withErrorBoundary` so a part-effect throw
     * replaces the live tree instead of escaping the flush.
     */
    onError = null;
    lifecycleEffects = null;
    lifecycleEffectsStarted = false;
    /** True while this instance is counted in `template.liveInstances` (#1375). */
    holdsTemplate = false;
    /**
     * Identity of the current bind, 0 while unmounted (#1335).
     *
     * Runtime-owned: a materialization captures it and compares it back to
     * decide whether it still owns this instance. Every bind stamps a fresh one
     * and `unmount` clears it, so a stale cleanup can neither tear out an
     * instance that has been re-mounted elsewhere nor unmount one twice.
     */
    mountEpoch = 0;
    /** True after the initial part-effect install finishes without throwing. */
    effectsBound = false;
    /**
     * Set when containment tears this instance down; remaining hole commits
     * no-op. Internal seam: the compiled install walk reads it (#1584).
     */
    commitAborted = false;
    /** Internal: the hydration path needs the same adapter this instance mounts with. */
    domAdapter;
    /** Owner for part effects and user work created under this instance. */
    owner;
    /** Owner that was current when this instance was constructed; remount reuses it. */
    ownerParent;
    /** Optional hole-update error sink used by `withErrorBoundary`. */
    commitError = null;
    constructor(template, bindings, domAdapter) {
        this.template = template;
        this.bindings = bindings;
        this.domAdapter = domAdapter;
        this.ownerParent = getOwner();
        this.owner = createOwner(this.ownerParent);
    }
    get html() {
        if (!templateHtmlRenderer) {
            throw runtimeError('RT411', 'TemplateInstance.html needs the string renderer; load the hydrogen barrel or hydrogen/render first');
        }
        return templateHtmlRenderer(this);
    }
    mount(container) {
        this.bindClone(container);
        return () => this.unmount();
    }
    /**
     * Internal: mounts with the clone's OWN fragment as the container, and
     * returns it.
     *
     * A caller that only needs a detached parent to hold the instance until it
     * is placed — `mountInstanceNodes`, and through it every keyed-repeat row —
     * used to create an empty fragment and have the mount move the clone's
     * children into it. The clone already is a detached parent, so that move
     * bought nothing and cost one relocation of every node in the row. Callers
     * that mount into a real target keep the ordinary path.
     *
     * Null means the mount was contained by an error boundary before a fragment
     * existed; the caller falls back to the instance's own (empty) node list,
     * exactly as a contained `mount` leaves it.
     *
     * `reused` is the caller declaring that this template is being instantiated
     * as one of a SET — a keyed repeat's row. Consume-on-first exists because a
     * template with one instantiation can never amortise a parse; a repeat is a
     * statement that the second instantiation is already on its way, so the
     * cache is worth building now rather than one parse later (#1375). Measured:
     * without this, cold `create1k` pays +0.207 ms [+0.110, +0.275] (n=100) for
     * one parse it throws away, and recovers no nodes for it.
     */
    mountOwnFragment(reused) {
        return this.bindClone(null, reused);
    }
    /**
     * Counts this instance against its template's parse.
     *
     * The count is what tells `ensureParse` that a second live instantiation has
     * arrived and the parse is therefore worth caching — see
     * `render-dom-parse.ts` for the rule, its price in retained nodes and the
     * measurements behind it (#1375). Only mounts whose decision depends on the
     * count register here; one that already declared itself `reused` does not.
     *
     * Internal: `render-hydrate.ts` drives the same sequence against server DOM,
     * so it calls `beginMount` too — an adopted instance keeps its template's
     * parse alive for exactly as long as a mounted one does.
     */
    retainTemplate() {
        if (this.holdsTemplate)
            return;
        this.holdsTemplate = true;
        const template = this.template;
        template.liveInstances = (template.liveInstances ?? 0) + 1;
    }
    /**
     * Gives up this instance's claim, and deliberately does NOT drop the parse
     * when the count reaches zero — an omission with a measurement behind it,
     * recorded with the rest of the parse policy in `render-dom-parse.ts`
     * (#1375).
     */
    releaseTemplate() {
        if (!this.holdsTemplate)
            return;
        this.holdsTemplate = false;
        const template = this.template;
        template.liveInstances = (template.liveInstances ?? 1) - 1;
    }
    /**
     * Opens a mount: a fresh ownership identity, and one claim on the template's
     * parse. Internal, shared with the hydration path.
     */
    beginMount(reused) {
        this.commitAborted = false;
        this.mountEpoch = ++nextMountEpoch;
        // A reused mount caches unconditionally and never consults the count, so
        // there is nothing for it to register. Keeping it accurate for a thousand
        // keyed rows is a shared read-modify-write per row that nothing reads.
        if (!reused)
            this.retainTemplate();
    }
    /**
     * Follows this instance's nodes to wherever a caller placed them.
     *
     * `mountOwnFragment` leaves an instance in the fragment its clone arrived in,
     * which is a real container until the caller moves the nodes into the page —
     * after which its only remaining job was saying who owned the instance.
     * `mountEpoch` says that now (#1335), so `container` is free to describe
     * where the nodes ARE and the drained fragment is free to be collected, with
     * the host `<template>` and contents-owner document a consumed parse brought
     * with it. Called by the placement sites after insertion, with the parent
     * they inserted into — reading it back off the nodes would be a DOM accessor
     * per row.
     */
    adoptPlacement(parent) {
        if (parent !== this.container)
            this.container = parent;
    }
    /**
     * Clones the template, binds its parts, installs its effects, and leaves the
     * result in `container` — or, when `container` is null, in the clone's own
     * fragment, which is then this instance's container.
     */
    bindClone(container, reused) {
        // Remounting first releases every resource owned by the previous target.
        // First mount must not dispose the constructor owner — interpolation
        // effects are already registered there.
        if (this.container !== null || this.parts.length > 0 || this.nodes.length > 0) {
            this.unmount();
        }
        this.commitAborted = false;
        try {
            const template = this.template;
            // Cloned from a cache, or the parse itself when nothing will clone it
            // again — `render-dom-parse.ts` owns that decision and its evidence.
            const templateElement = ensureParse(template, this.domAdapter, reused);
            let fragment = takeFragment(template, templateElement);
            // Opened before the parts bind, so a nested instantiation of this same
            // template sees a live instance and caches instead of consuming again.
            this.beginMount(reused);
            if (this.template.namespace === 'svg') {
                fragment = unwrapNamespaceWrapper(fragment);
            }
            // `documentObj` must stay the document the instance is rendering INTO.
            // A clone of `template.content` belongs to the template's inert contents
            // document, so deriving it from the clone would make every comment and
            // text node this mount creates a foreign-document node — exactly the
            // corruption #1244 is about. Only a real container speaks for a
            // document; the own-fragment path takes the page's, as it always did.
            const documentObj = container?.ownerDocument || globalThis.document || templateElement.ownerDocument;
            this.container = container ?? fragment;
            // The compiled seam (#1584): the nav module registers the binder at
            // import; the nav-field guard keeps payload-less templates from ever
            // building the call, and a missing binder or rejected payload (the
            // verdict is memoized per template) takes the recorded-plan path.
            if (template.nav !== undefined &&
                compiledNavBinder()?.(this, fragment, container, documentObj)) {
                this.effectsBound = true;
                this.startLifecycleEffects();
                return fragment;
            }
            const prepared = prepareDomParts(this.template, fragment, documentObj, this.domAdapter, this.bindings);
            this.parts = prepared.parts;
            this.staticIndices = prepared.staticIndices.length > 0 ? prepared.staticIndices : null;
            this.staticRefs = prepared.staticRefs.length > 0 ? prepared.staticRefs : null;
            this.staticPlan = this.staticIndices ? prepared.plan : null;
            this.indexParts();
            this.nodes = childNodesOf(fragment);
            if (container !== null) {
                this.domAdapter.replaceChildren(container, fragment);
            }
            this.installPartEffects();
            this.effectsBound = true;
            this.startLifecycleEffects();
            return fragment;
        }
        catch (error) {
            const handler = this.containmentHandler();
            if (handler) {
                handler(error);
                return null;
            }
            throw error;
        }
    }
    /**
     * Splits parts into the two commit passes once, not per commit (#686).
     *
     * Internal: `render-hydrate.ts` drives the same sequence as `mount` against
     * server DOM. It calls this rather than duplicating it — see `hydrateInstance`
     * for why hydration is a free function and not a method here.
     */
    indexParts() {
        // A stable partition of `parts` in place, rather than two more arrays
        // holding the same objects: node-family parts keep their relative order
        // ahead of every attribute-family and element part, which keep theirs.
        // Every later pass — install, commit, destroy — is then a single walk of
        // `parts` that is already in G10 order, and the instance retains one
        // array where it used to retain three. A 1,000-row list was paying two
        // JSArrays and two element stores per row for the duplication.
        const parts = this.parts;
        const deferred = [];
        let write = 0;
        for (const part of parts) {
            if (isDeferredPart(part))
                deferred.push(part);
            else
                parts[write++] = part;
        }
        for (const part of deferred)
            parts[write++] = part;
    }
    /** Internal: the owner the static-slot walk in render-dom-static-slots.ts effects run under. */
    ensureOwner() {
        if (this.owner.disposed)
            this.owner = createOwner(this.ownerParent);
        return this.owner;
    }
    /**
     * Creates the tracked effect that drives one part. The caller establishes
     * the owner.
     *
     * Part commits are DOM writes, so they run in the flush's write phase —
     * after every read-only effect in the same batch (#685).
     */
    // Internal seam: shared with the static-slot walk, as `indexParts` is.
    createPartEffect(part) {
        return effect(() => {
            if (this.commitAborted)
                return;
            this.commitPart(part, getBinding(this.bindings, part.spec));
        }, { commitsDom: true });
    }
    /** Internal: shared with the hydration path, as `indexParts` is. */
    installPartEffects() {
        runWithOwner(this.ensureOwner(), () => {
            // Node-family commits first so `<option>` content exists before a
            // sibling `.value`/`?selected` binding reads the DOM to match it (G10).
            // Static slots, static parts and reactive parts share this one walk, so
            // every commit — slotted or part — lands in exactly its G10 position.
            const parts = this.parts;
            const deferredStart = this.deferredPartStart();
            let slot = 0;
            for (let pass = 0; pass < 2; pass += 1) {
                const nodePass = pass === 0;
                const start = nodePass ? 0 : deferredStart;
                const end = nodePass ? deferredStart : parts.length;
                for (let p = start; p < end; p += 1) {
                    const part = parts[p];
                    if (!part)
                        continue;
                    if (this.commitAborted)
                        return;
                    slot = commitStaticSlotsBefore(this, slot, part.spec.index, nodePass);
                    // A binding that provably cannot change gets no reactive node, no
                    // closure and no disposer — just its one commit, here. This is the
                    // hydration path's shape: its statics arrive as part objects, since
                    // adoption runs before any binding is known.
                    //
                    // Committing it synchronously outside the effect system does not
                    // break the write-phase rule (#685): `effect()` runs its callback
                    // immediately on creation, never through `queuedEffects`, so a part's
                    // FIRST commit has never been ordered by the flush's write phase.
                    // Only re-runs are, and a static part has none by construction. This
                    // commit therefore lands at exactly the instant, and in exactly the
                    // order, the elided effect's own first run would have.
                    const value = getBinding(this.bindings, part.spec);
                    if (isStaticBinding(part.spec, value)) {
                        this.commitPart(part, value);
                        continue;
                    }
                    // Signals are resolved inside the effect by the shared value semantics.
                    part.disposer = this.createPartEffect(part);
                }
                // Drain the slots that sort after every part of this family.
                slot = commitStaticSlotsBefore(this, slot, Number.POSITIVE_INFINITY, nodePass);
            }
        });
    }
    /**
     * The index of the first attribute-family part, which `indexParts`'s stable
     * partition put after every node-family part. The install and update walks
     * split into two passes at this boundary.
     */
    deferredPartStart() {
        const parts = this.parts;
        let start = 0;
        while (start < parts.length) {
            const part = parts[start];
            if (!part || isDeferredPart(part))
                break;
            start += 1;
        }
        return start;
    }
    /** Internal registration seam used by the public `withLifecycle` helper. */
    registerLifecycleEffects(effects) {
        if (this.container !== null) {
            throw runtimeError('RT401', 'withLifecycle must run before the template instance mounts');
        }
        (this.lifecycleEffects ??= []).push(...effects);
    }
    /** Internal: hydration starts the same registered effects as a normal mount. */
    startLifecycleEffects() {
        if (this.lifecycleEffectsStarted)
            return;
        this.lifecycleEffectsStarted = true;
        const definitions = this.lifecycleEffects;
        if (!definitions)
            return;
        runWithOwner(this.ensureOwner(), () => {
            for (const definition of definitions) {
                let initialized = false;
                let previousValues = [];
                let cleanup = null;
                // The running setup is owned HERE, not by the effect's own run
                // scope: the kernel resets that scope before every body run, so a
                // setup owned there would be torn down before the equality check
                // below could skip the restart (#1415) — its onCleanup run and its
                // nested effects disposed on a transition whose declared dependency
                // value never changed, leaving the view silently dead.
                let setupOwner = null;
                effect(() => {
                    const nextValues = definition.dependencies.map((dependency) => dependency());
                    const changed = !initialized ||
                        nextValues.length !== previousValues.length ||
                        nextValues.some((value, index) => !Object.is(value, previousValues[index]));
                    // An equal-value transition is a lifecycle no-op: the setup and
                    // everything it created stay live.
                    if (!changed)
                        return;
                    // Replacing a generation tears it down inner-to-outer (#1203):
                    // the setup's registered cleanups and nested effects first, then
                    // the disposer the setup itself returned.
                    if (setupOwner) {
                        disposeOwner(setupOwner);
                        setupOwner = null;
                    }
                    cleanup?.();
                    cleanup = null;
                    initialized = true;
                    previousValues = nextValues;
                    setupOwner = createOwner(this.ensureOwner());
                    const nextCleanup = runWithOwner(setupOwner, () => untrack(definition.setup));
                    if (typeof nextCleanup === 'function')
                        cleanup = nextCleanup;
                }, 
                // Renderer part effects register before lifecycle effects, so this
                // write-phase callback observes the DOM and refs AFTER a dependency
                // change has committed its conditional/list/property updates.
                { commitsDom: true });
                onCleanup(() => {
                    if (setupOwner) {
                        disposeOwner(setupOwner);
                        setupOwner = null;
                    }
                    cleanup?.();
                    cleanup = null;
                });
            }
        });
    }
    containmentHandler() {
        return this.onError ?? this.commitError;
    }
    commitPart(part, value) {
        if (this.commitAborted)
            return;
        try {
            part.update(value);
        }
        catch (error) {
            // During the initial mount install, rethrow so `mount` can contain it.
            // After the instance is live, a later hole update must not escape the flush.
            const handler = this.containmentHandler();
            if (handler && this.effectsBound) {
                this.commitAborted = true;
                handler(error);
                return;
            }
            throw error;
        }
    }
    update(bindings) {
        const previous = this.bindings;
        this.bindings = bindings;
        // Only parts whose binding actually changed commit; an unchanged part
        // keeps its live effect and its DOM untouched. This is what makes a keyed
        // repeat's per-row update O(changed holes), not O(rows × holes) (#678).
        // Node-family before attribute-family preserves the mount ordering (G10).
        this.commitChangedParts(previous);
    }
    commitChangedParts(previous) {
        try {
            const parts = this.parts;
            const deferredStart = this.deferredPartStart();
            let slot = 0;
            for (let pass = 0; pass < 2; pass += 1) {
                const nodePass = pass === 0;
                const start = nodePass ? 0 : deferredStart;
                const end = nodePass ? deferredStart : parts.length;
                for (let p = start; p < end; p += 1) {
                    const part = parts[p];
                    if (!part)
                        continue;
                    if (this.commitAborted)
                        return;
                    slot = updateStaticSlotsBefore(this, slot, part.spec.index, previous, nodePass);
                    const next = getBinding(this.bindings, part.spec);
                    if (bindingUnchanged(getBinding(previous, part.spec), next))
                        continue;
                    const disposer = part.disposer;
                    if (!disposer) {
                        // No disposer means the part holds no effect, because its binding
                        // was static when it was installed (hydration's shape). If the new
                        // binding is static too, one direct commit is again the whole job.
                        // If it is not — a signal has arrived where a constant used to be —
                        // the part must gain a tracked effect now, or that signal would
                        // never re-trigger it (#678).
                        if (isStaticBinding(part.spec, next)) {
                            this.commitPart(part, next);
                            continue;
                        }
                        part.disposer = runWithOwner(this.ensureOwner(), () => this.createPartEffect(part));
                        continue;
                    }
                    // Re-create the part's effect rather than committing directly: the
                    // effect's tracked dependency set must follow the new binding, or a
                    // signal introduced by this update would never re-trigger the part
                    // (#678). `effect` runs synchronously, so the commit stays synchronous.
                    disposer();
                    part.disposer = runWithOwner(this.ensureOwner(), () => this.createPartEffect(part));
                }
                slot = updateStaticSlotsBefore(this, slot, Number.POSITIVE_INFINITY, previous, nodePass);
            }
        }
        finally {
            // A promotion stages its part rather than mutating `parts` mid-walk: the
            // walk above holds cursors over this array, and re-partitioning under
            // them would double-commit one part and skip another. Staged parts have
            // already committed — their effect's first run is synchronous — so the
            // flush is bookkeeping, not a second commit.
            const promoted = this.promotedParts;
            if (promoted) {
                this.promotedParts = null;
                // Two conditions guard this flush. `commitAborted` covers containment:
                // a walk that aborted mid-family must not mutate the array its cursors
                // still describe. `mountEpoch` covers teardown (#1440): a promoted
                // part whose first commit threw into a boundary whose handler unmounted
                // this instance leaves the instance dead — `parts` emptied, ownership
                // disposed — and a flush that repopulates it would hang
                // destroyed-owner parts with live DOM refs off the instance for the
                // next `bindClone` to destroy a second time.
                if (!this.commitAborted && this.mountEpoch !== 0) {
                    for (const part of promoted)
                        this.insertPromotedPart(part);
                    this.indexParts();
                }
            }
        }
    }
    /**
     * Inserts a promoted part at its `spec.index` position within its commit
     * family (#1439). The commit walks use `spec.index` as the slot cursor's
     * `beforeIndex` and assume `parts` is ascending in `spec.index` within each
     * family, so a tail append would commit later holes before earlier ones
     * after a second promotion. Families are contiguous — `indexParts` leaves
     * node-family parts ahead of deferred ones — so the family bounds are one
     * `deferredPartStart` read, and a promotion happens once per slot per
     * instance lifetime: the shift is bounded by the part count, not by updates.
     */
    insertPromotedPart(part) {
        const parts = this.parts;
        let start = 0;
        let end = parts.length;
        if (isDeferredPart(part)) {
            start = this.deferredPartStart();
        }
        else {
            end = this.deferredPartStart();
        }
        let at = start;
        while (at < end && parts[at].spec.index < part.spec.index)
            at += 1;
        parts.splice(at, 0, part);
    }
    unmount() {
        this.commitAborted = true;
        this.effectsBound = false;
        this.lifecycleEffectsStarted = false;
        // A cleared epoch retires this mount's identity: a materialization holding
        // the old one no longer owns anything (#1335).
        this.mountEpoch = 0;
        this.releaseTemplate();
        // Owner dispose runs part-effect and user cleanups; the parts leaving here
        // carry the same disposers, so they are dropped rather than called again.
        disposeOwner(this.owner);
        // Detached, not spliced: the splice copied every element into a drain
        // array nothing reads, once per instance, which a 1,000-row teardown paid
        // a thousand times. Handing the array off gives the same "iterate what was
        // there when teardown started" guarantee for one empty array.
        const parts = this.parts;
        this.parts = [];
        for (const part of parts) {
            part.destroy();
            // Dropped, not called — `disposeOwner` above already ran this effect's
            // cleanup. Clearing it is what the `disposers` array this replaced got
            // for free from being spliced (#1363): the field is reachable FROM the
            // part, and `updateAsyncPart`/`updateUntilPart` hand the part to a
            // a thenable continuation that outlives the unmount. Left set, an
            // `until()` whose promise never settles retains the disposer, its
            // observer, this instance, and every value bound into it.
            part.disposer = undefined;
        }
        // An enclosing detach (#1311) already owns every one of these nodes: the
        // repeat that is dropping this row removes its whole node set itself, so
        // removing them here would be the same mutation twice.
        //
        // The list is DETACHED, not drained in place. Anything still holding it —
        // a materialization, a repeat entry — keeps the nodes it was handed
        // (#1332), which is what lets `mountInstanceNodes` hand out this array
        // itself rather than a defensive copy of it per mounted instance.
        const detached = this.nodes;
        this.nodes = [];
        if (!isSubtreeDetaching()) {
            for (const node of detached) {
                if (node.parentNode)
                    node.parentNode.removeChild(node);
            }
        }
        // The static slots hold no effects and no listeners — only DOM references
        // a live mount still needs — so they are dropped, not destroyed (#1398).
        this.staticIndices = null;
        this.staticRefs = null;
        this.staticPlan = null;
        this.promotedParts = null;
        this.container = null;
    }
}
export function instantiateTemplate(template, bindings, domAdapter) {
    return new TemplateInstanceImpl(template, bindings, domAdapter);
}
export function mountTemplate(target, instance, domAdapter) {
    if (!target) {
        throw runtimeError('RT401', 'mount target missing');
    }
    if (isTemplateInstanceValue(instance)) {
        return instance.mount(target);
    }
    if (isLegacyComponent(instance)) {
        return mountLegacyComponent(target, instance, domAdapter);
    }
    throw runtimeError('RT401', 'mount requires a template instance');
}
//# sourceMappingURL=render-dom.js.map